import type { components } from '@/lib/api/generated/schema'
import { api, normalizePrivateProfile } from '@/lib/api'

type Schemas = components['schemas']

export type PrivateProfile = Schemas['PrivateProfile']

export type ActivityDetail = Schemas['ActivityDetail']
export type Participant = Schemas['Participant']
export type PublicProfile = Schemas['PublicProfile']
export type ReportReason = Schemas['ReportReason']
export type CreateReportRequest = Schemas['CreateReportRequest']
export type SavedStateResponse = Schemas['SavedStateResponse']
export type JoinActivityResponse = Schemas['JoinActivityResponse']
export type LeaveActivityResponse = Schemas['LeaveActivityResponse']
export type JoinRequest = Schemas['JoinRequest']
export type JoinRequestList = Schemas['JoinRequestList']
export type AcceptJoinRequestResponse = Schemas['AcceptJoinRequestResponse']
export type RejectJoinRequestResponse = Schemas['RejectJoinRequestResponse']
export type FeedbackRequest = Schemas['FeedbackRequest']
export type FeedbackResponse = Schemas['FeedbackResponse']
export type AttendanceStatus = NonNullable<Schemas['Participant']['attendanceStatus']>

interface RequestOptions {
  signal?: AbortSignal
}

function inviteQuery(inviteToken: string | null) {
  return inviteToken ? { invite: inviteToken } : undefined
}

/**
 * Thin feature boundary over the frozen activity/profile/report endpoints.
 * Responses are never cached here: the backend owns server state (D-042) and
 * the detail screen reconciles each mutation explicitly.
 */
export const activityDetailApi = {
  getViewerProfile(options: RequestOptions = {}): Promise<PrivateProfile> {
    return api
      .get<PrivateProfile>('/me', { ...options })
      .then(normalizePrivateProfile)
  },

  getActivity(
    activityId: string,
    inviteToken: string | null,
    options: RequestOptions = {},
  ): Promise<ActivityDetail> {
    return api.get<ActivityDetail>(
      `/activities/${encodeURIComponent(activityId)}`,
      {
        query: inviteQuery(inviteToken),
        signal: options.signal,
      },
    )
  },

  getPublicProfile(
    userId: string,
    options: RequestOptions = {},
  ): Promise<PublicProfile> {
    return api.get<PublicProfile>(`/users/${encodeURIComponent(userId)}`, {
      signal: options.signal,
    })
  },

  listParticipants(
    activityId: string,
    inviteToken: string | null,
    options: RequestOptions = {},
  ): Promise<Schemas['ParticipantList']> {
    return api.get<Schemas['ParticipantList']>(
      `/activities/${encodeURIComponent(activityId)}/participants`,
      {
        query: inviteQuery(inviteToken),
        signal: options.signal,
      },
    )
  },

  save(activityId: string): Promise<SavedStateResponse> {
    return api.post<SavedStateResponse>(
      `/activities/${encodeURIComponent(activityId)}/save`,
    )
  },

  unsave(activityId: string): Promise<SavedStateResponse> {
    return api.delete<SavedStateResponse>(
      `/activities/${encodeURIComponent(activityId)}/save`,
    )
  },

  report(body: CreateReportRequest): Promise<Schemas['CreateReportResponse']> {
    return api.post<Schemas['CreateReportResponse']>('/reports', body)
  },

  blockUser(userId: string): Promise<Schemas['BlockResponse']> {
    return api.post<Schemas['BlockResponse']>(
      `/users/${encodeURIComponent(userId)}/block`,
    )
  },

  join(
    activityId: string,
    inviteToken: string | null,
  ): Promise<JoinActivityResponse> {
    return api.post<JoinActivityResponse>(
      `/activities/${encodeURIComponent(activityId)}/join`,
      inviteToken ? { inviteToken } : undefined,
    )
  },

  leave(activityId: string): Promise<LeaveActivityResponse> {
    return api.delete<LeaveActivityResponse>(
      `/activities/${encodeURIComponent(activityId)}/join`,
    )
  },

  cancel(activityId: string): Promise<Schemas['OkResponse']> {
    return api.delete<Schemas['OkResponse']>(
      `/activities/${encodeURIComponent(activityId)}`,
    )
  },

  stopRecurrence(activityId: string): Promise<{ cancelledOccurrences?: number }> {
    return api.post<{ cancelledOccurrences?: number }>(
      `/activities/${encodeURIComponent(activityId)}/recurrence/stop`,
    )
  },

  setParticipantAttendance(
    activityId: string,
    participantId: string,
    attendanceStatus: AttendanceStatus,
  ): Promise<{ attendanceStatus: AttendanceStatus }> {
    return api.patch<{ attendanceStatus: AttendanceStatus }>(
      `/activities/${encodeURIComponent(activityId)}/participants/${encodeURIComponent(participantId)}/attendance`,
      { attendanceStatus },
    )
  },

  listJoinRequests(
    activityId: string,
    options: RequestOptions = {},
  ): Promise<JoinRequestList> {
    return api.get<JoinRequestList>(
      `/activities/${encodeURIComponent(activityId)}/join-requests`,
      { signal: options.signal },
    )
  },

  acceptJoinRequest(
    activityId: string,
    requestId: string,
  ): Promise<AcceptJoinRequestResponse> {
    return api.post<AcceptJoinRequestResponse>(
      `/activities/${encodeURIComponent(activityId)}/join-requests/${encodeURIComponent(requestId)}/accept`,
    )
  },

  rejectJoinRequest(
    activityId: string,
    requestId: string,
    reason?: string,
  ): Promise<RejectJoinRequestResponse> {
    const body = reason?.trim() ? { reason: reason.trim() } : undefined
    return api.post<RejectJoinRequestResponse>(
      `/activities/${encodeURIComponent(activityId)}/join-requests/${encodeURIComponent(requestId)}/reject`,
      body,
    )
  },

  submitFeedback(
    activityId: string,
    body: FeedbackRequest,
  ): Promise<FeedbackResponse> {
    return api.post<FeedbackResponse>(
      `/activities/${encodeURIComponent(activityId)}/feedback`,
      body,
    )
  },
}
